import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import * as JsBarcode from 'jsbarcode';
import * as QRCode from 'qrcode';
import { BadgeReaderService } from '../../../_services/badge-reader.service';
import { BadgeService } from '../../../_services/badge.service';
import { Badge, BadgeLivelloSicurezza } from '../../../_models/badge.model';
import { SaveMessage } from '../../../_helpers/save-message';
import { MessageConfirm } from '../../../_helpers/message-confirm.service';
import { AccountOrganizzazione } from '../../../_models/AccountOrganizzazione';

type BadgeEnrollMode = 'fisica' | 'genera';

@Component({
  selector: 'app-badge-enroll',
  templateUrl: './badge-enroll.component.html',
  styleUrls: ['./badge-enroll.component.scss'],
  standalone: false
})
export class BadgeEnrollComponent implements OnInit, OnDestroy {
  @Input() operatore: AccountOrganizzazione;
  @ViewChild('symbolCanvas') symbolCanvas?: ElementRef<HTMLCanvasElement>;

  badges: Badge[] = [];
  codiceBadge = '';
  descrizione = '';
  loading = false;

  mode: BadgeEnrollMode = 'fisica';
  livelloSicurezza: number = BadgeLivelloSicurezza.Standard;
  descrizioneGen = '';
  generatedBadge?: Badge;
  badgePreviewMode: BadgeEnrollMode | null = null;

  protected readonly BadgeLivelloSicurezza = BadgeLivelloSicurezza;
  private badgeSub?: Subscription;

  constructor(
    private badgeReader: BadgeReaderService,
    private badgeService: BadgeService,
    private saveMessage: SaveMessage,
    private deleteConfirm: MessageConfirm,
    private cdr: ChangeDetectorRef,
    private modalCtrl: ModalController) {}

  ngOnInit() {
    this.badgeReader.enable();
    this.badgeSub = this.badgeReader.code$.subscribe(code => this.codiceBadge = code);
    this.loadBadges();
  }

  ngOnDestroy() {
    this.badgeSub?.unsubscribe();
    this.badgeReader.disable();
  }

  loadBadges = () => {
    this.loading = true;
    this.badgeService.listBadgesByOperatore(this.operatore.accountId)
      .then(list => this.badges = list)
      .finally(() => this.loading = false);
  };

  confirm = () => {
    if (!this.codiceBadge) {
      this.saveMessage.messageError('Avvicina un badge al lettore prima di confermare.');
      return;
    }
    this.loading = true;
    this.badgeService.registerBadge({
      operatoreId: this.operatore.accountId,
      codiceBadge: this.codiceBadge,
      descrizione: this.descrizione || undefined
    })
      .then(() => { this.codiceBadge = ''; this.descrizione = ''; this.loadBadges(); })
      .catch(err => this.saveMessage.messageError(err?.error?.message || 'Badge già in uso'))
      .finally(() => this.loading = false);
  };

  generate = () => {
    this.loading = true;
    this.badgeService.generateBadge({
      operatoreId: this.operatore.accountId,
      descrizione: this.descrizioneGen || undefined,
      livelloSicurezza: this.livelloSicurezza
    })
      .then(badge => {
        this.generatedBadge = badge;
        this.badgePreviewMode = 'genera';
        this.descrizioneGen = '';
        this.loadBadges();
        return this.renderSymbol(badge);
      })
      .catch(err => this.saveMessage.messageError(err?.error?.message || 'Impossibile generare il codice'))
      .finally(() => this.loading = false);
  };

  print = () => this.printCard();

  reprint = (badge: Badge) => {
    this.generatedBadge = badge;
    this.badgePreviewMode = 'fisica';
    this.renderSymbol(badge).then(() => this.printCard());
  };

  // Marca il body durante la stampa così le regole @media print isolano la sola tessera,
  // senza lasciar trapelare lo stile globale alle stampe di altre pagine.
  private printCard() {
    document.body.classList.add('printing-badge');
    const cleanup = () => {
      document.body.classList.remove('printing-badge');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  }

  isGenerated = (badge: Badge) => badge.livelloSicurezza != null;

  private renderSymbol = async (badge: Badge): Promise<void> => {
    this.cdr.detectChanges(); // assicura che il <canvas> del *ngIf sia nel DOM
    const canvas = this.symbolCanvas?.nativeElement;
    if (!canvas) return;
    if (badge.livelloSicurezza === BadgeLivelloSicurezza.Alta) {
      await QRCode.toCanvas(canvas, badge.codiceBadge, { width: 220, margin: 2 });
    } else {
      JsBarcode(canvas, badge.codiceBadge, { format: 'CODE128', displayValue: false, height: 80 });
    }
  };

  manageBadge = (badge: Badge) => badge.revoked
    ? this.deleteConfirm.presentAlert('Eliminare definitivamente questo badge? La revoca potrebbe non arrivare ai sistemi collegati.', () => this.doDelete(badge.id))
    : this.revoke(badge);

  private doDelete = (id: number) => {
    this.loading = true;
    this.badgeService.deleteRevokedBadge(id)
      .then(() => this.loadBadges())
      .catch(err => this.saveMessage.messageError(err?.error?.message || 'Impossibile eliminare il badge'))
      .finally(() => this.loading = false);
  };

  revoke = (badge: Badge) => this.deleteConfirm
    .presentAlert('Revocare questo badge?', () => this.doRevoke(badge.id));

  private doRevoke = (id: number) => {
    this.loading = true;
    this.badgeService.revokeBadge(id)
      .then(() => this.loadBadges())
      .catch(err => this.saveMessage.messageError(err?.error?.message || 'Impossibile revocare il badge'))
      .finally(() => this.loading = false);
  };

  close = () => this.modalCtrl.dismiss(true, 'confirm');
}
